import {AppRouteEnum} from 'src/utils/helper';

/**
 * What each page needs, in one place.
 *
 * The route guard and the menu entry that leads to it must ask the same question, and the only way to
 * guarantee that is for both to read the same constant. They did not, and every "I can see it but it
 * says no access" came from the gap: the menu asked CASL `can('read', 'users')`, which is satisfied by
 * *any* `users.*` permission, while the page required `users.search` specifically. Holding
 * `users.read` — permission to open one person — was enough to light up a menu entry leading to a
 * list nobody was allowed to fetch.
 *
 * These are the list pages, the ones that appear in navigation. Detail pages keep their own guards in
 * `routes.tsx`, since nothing links to them from a menu.
 */
export const PAGE_PERMISSION: Record<string, string | readonly string[]> = {
    [AppRouteEnum.locations]: 'locations.search',
    [AppRouteEnum.chargers]: 'chargers.search',
    [AppRouteEnum.chargingSessions]: 'transactions.search',
    [AppRouteEnum.users]: 'users.search',
    [AppRouteEnum.employee]: 'employees.search',
    [AppRouteEnum.rfid]: 'rfidTags.search',
    [AppRouteEnum.contracts]: 'contracts.search',
    [AppRouteEnum.tariffs]: 'tariffs.search',
    // The statistics screen, which calls none of the reports.read endpoints: it draws consumption
    // from report-service (authenticated, no permission of its own), charging sessions, and invoice
    // and contract summaries. Either half is enough to make the page worth opening - a driver has
    // their sessions, a sales manager has the reports side.
    [AppRouteEnum.reports]: ['transactions.search', 'reports.read'],
    [AppRouteEnum.invoices]: 'invoices.search',
    [AppRouteEnum.technicalTeam]: 'users.search',
    [AppRouteEnum.notifications]: 'notifications.search',
    [AppRouteEnum.reOwner]: 'realEstates.search',
    [AppRouteEnum.accessControl]: 'accessControl.readRoles',
    // Listing every tenant, which only somebody working across them can do.
    [AppRouteEnum.businesses]: 'businesses.readPlatform',
    // The assistant lives in another service, but who may open it is decided here.
    [AppRouteEnum.assistant]: 'agent.use',
} as const;

export type NavigablePage = keyof typeof PAGE_PERMISSION;
